import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

import notificationApi from '@/core/notifications/api/notification-api';
import { Contact } from '@/core/notifications/model';

const LABEL_MAX_LENGTH = 100;

// The form itself, with no modal chrome — same split as quick-edit-name-popover.tsx.
// Renames (or clears) a contact's cosmetic `label`, the human-friendly name
// shown instead of its raw identityValue (e.g. a Slack webhook URL) once its
// channel supports one. Purely cosmetic: PATCHes only `label`, never `config`.
export function EditContactLabelModalContent({
  contact,
  onClose,
  onSaved,
}: {
  contact: Contact;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(contact.label ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const trimmed = label.trim();
      await notificationApi.patchContact(contact.id, { label: trimmed ? trimmed : null });
      toast.success(trimmed ? 'Contact renamed' : 'Contact name cleared');
      onSaved();
    } catch (e) {
      toast.error('Could not update the contact name');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <label className="form-label" htmlFor="edit-contact-label-input">
        Name
      </label>
      <input
        id="edit-contact-label-input"
        className="form-control form-control-sm mb-1"
        autoFocus
        maxLength={LABEL_MAX_LENGTH}
        value={label}
        disabled={saving}
        onChange={event => setLabel(event.target.value)}
        onKeyDown={event => event.key === 'Enter' && handleSave()}
      />
      <div className="form-text mb-2">Leave blank to remove the name.</div>
      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-sm btn-tertiary text-base-light"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </>
  );
}

export default function EditContactLabelModal({
  contact,
  onClose,
  onSaved,
}: {
  contact: Contact;
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <Modal show onHide={onClose} centered size="sm" className="notifications-settings">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 14, fontWeight: 700 }}>Name this contact</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <EditContactLabelModalContent contact={contact} onClose={onClose} onSaved={onSaved} />
      </Modal.Body>
    </Modal>
  );
}
